import React, { useState } from 'react'

import AdminLayout from '../components/AdminLayout'

import './settings.css'

type SettingsTab = 'user' | 'role' | 'preference'

const Settings = () => {
  const [tab, setTab] = useState<SettingsTab>('user')
  const [emailNotif, setEmailNotif] = useState(true)
  const [smsAlerts, setSmsAlerts] = useState(false)
  const [compactTables, setCompactTables] = useState(false)

  return (
    <AdminLayout title="Settings" activeNav="settings" pageTitle="Settings - Namami Gange">
      <div className="settings-shell">
        <div className="settings-shell-tabs">
          <div className="settings-tabs settings-tabs--bar" role="tablist" aria-label="Settings sections">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'user'}
              id="settings-tab-user"
              aria-controls="settings-panel-user"
              className={`settings-tab settings-tab--bar${tab === 'user' ? ' settings-tab--active-bar' : ''}`}
              onClick={() => setTab('user')}
            >
              User
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'role'}
              id="settings-tab-role"
              aria-controls="settings-panel-role"
              className={`settings-tab settings-tab--bar${tab === 'role' ? ' settings-tab--active-bar' : ''}`}
              onClick={() => setTab('role')}
            >
              Role
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'preference'}
              id="settings-tab-preference"
              aria-controls="settings-panel-preference"
              className={`settings-tab settings-tab--bar${tab === 'preference' ? ' settings-tab--active-bar' : ''}`}
              onClick={() => setTab('preference')}
            >
              Preference
            </button>
          </div>
        </div>

        <div className="settings-shell-body">
          {tab === 'user' && (
            <div className="settings-split settings-split--user" role="presentation">
              <section
                className="settings-pane"
                role="tabpanel"
                id="settings-panel-user"
                aria-labelledby="settings-tab-user"
              >
                <h2 className="settings-pane-title">User profile</h2>
                <p className="settings-help settings-help--tight">
                  Shared with coordinators across your Namami Gange working groups.
                </p>

                <div className="settings-field-grid">
                  <div className="settings-field">
                    <label htmlFor="settings-full-name">Full name</label>
                    <input id="settings-full-name" type="text" defaultValue="Rajesh Nair" autoComplete="name" />
                  </div>
                  <div className="settings-field">
                    <label htmlFor="settings-email">Email</label>
                    <input
                      id="settings-email"
                      type="email"
                      defaultValue="rajesh.nair@example.gov.in"
                      autoComplete="email"
                    />
                  </div>
                  <div className="settings-field">
                    <label htmlFor="settings-phone">Phone</label>
                    <input id="settings-phone" type="tel" placeholder="+91 …" autoComplete="tel" />
                  </div>
                  <div className="settings-field">
                    <label htmlFor="settings-dept">Department</label>
                    <input id="settings-dept" type="text" placeholder="e.g. Operations" />
                  </div>
                </div>

                <div className="settings-actions">
                  <button type="button" className="settings-btn settings-btn--ghost">
                    Cancel
                  </button>
                  <button type="button" className="settings-btn settings-btn--primary">
                    Save changes
                  </button>
                </div>
              </section>

              <aside className="settings-profile-card" aria-label="Account summary">
                <div className="settings-profile-photo-wrap">
                  <img src="/image1120-bwth-200h.png" alt="" className="settings-profile-avatar" />
                  <button type="button" className="settings-link-btn">
                    Change photo
                  </button>
                </div>
                <div className="settings-profile-name">Rajesh Nair</div>
                <div className="settings-profile-role">Super Admin</div>
                <dl className="settings-profile-meta">
                  <div>
                    <dt>Last sign-in</dt>
                    <dd>Today · 09:14</dd>
                  </div>
                  <div>
                    <dt>Authenticator</dt>
                    <dd>Enabled</dd>
                  </div>
                  <div>
                    <dt>Primary site</dt>
                    <dd>PMU · Haridwar</dd>
                  </div>
                </dl>
              </aside>
            </div>
          )}

          {tab === 'role' && (
            <section
              className="settings-pane"
              role="tabpanel"
              id="settings-panel-role"
              aria-labelledby="settings-tab-role"
            >
              <h2 className="settings-pane-title">Role & access</h2>
              <p className="settings-help settings-help--tight">
                Your administrator assigns roles and data scope. Request changes through PMU IT.
              </p>

              <div className="settings-summary-row">
                <div className="settings-summary-chip">
                  <span className="settings-summary-label">Current role</span>
                  <span className="settings-badge settings-badge--lg">Super Admin</span>
                </div>
              </div>

              <div className="settings-field-grid settings-field-grid--narrow settings-field-grid--role">
                <div className="settings-field">
                  <label htmlFor="settings-scope">Data scope</label>
                  <select id="settings-scope" defaultValue="all" disabled>
                    <option value="all">All districts</option>
                    <option value="district">Single district</option>
                    <option value="site">Single site</option>
                  </select>
                </div>
                <div className="settings-field settings-field--span2">
                  <label htmlFor="settings-modules">Primary modules</label>
                  <input
                    id="settings-modules"
                    type="text"
                    defaultValue="Dashboard, Plants, Reports, Monitoring, CCTV"
                    disabled
                  />
                </div>
              </div>

              <h3 className="settings-subhead">Effective permissions</h3>
              <div className="settings-access-table-wrap">
                <table className="settings-access-table">
                  <thead>
                    <tr>
                      <th>Module</th>
                      <th>View</th>
                      <th>Edit</th>
                      <th>Export</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Dashboard', true, true, true],
                      ['Plants & contracts', true, true, true],
                      ['Reports', true, true, true],
                      ['Monitoring', true, false, false],
                      ['Collaboration', true, true, false],
                      ['Master data', true, false, false],
                    ].map(([name, view, edit, exp]) => (
                      <tr key={String(name)}>
                        <td>{name}</td>
                        <td>
                          <span className={`settings-check${view ? '' : ' settings-check--no'}`}>
                            {view ? '✓' : '—'}
                          </span>
                        </td>
                        <td>
                          <span className={`settings-check${edit ? '' : ' settings-check--no'}`}>
                            {edit ? '✓' : '—'}
                          </span>
                        </td>
                        <td>
                          <span className={`settings-check${exp ? '' : ' settings-check--no'}`}>
                            {exp ? '✓' : '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {tab === 'preference' && (
            <section
              className="settings-pane"
              role="tabpanel"
              id="settings-panel-preference"
              aria-labelledby="settings-tab-preference"
            >
              <h2 className="settings-pane-title">Preferences</h2>
              <p className="settings-help settings-help--tight">
                Stored on this browser only unless your organization syncs profile settings.
              </p>

              <div className="settings-pref-block">
                <h3 className="settings-subhead">Notifications</h3>
                <div className="settings-row settings-row--wide">
                  <div>
                    <span className="settings-row-title">Email notifications</span>
                    <span className="settings-row-caption">Operational alerts and summaries</span>
                  </div>
                  <button
                    type="button"
                    className={`settings-toggle${emailNotif ? ' settings-toggle--on' : ''}`}
                    onClick={() => setEmailNotif((v) => !v)}
                    aria-pressed={emailNotif}
                    aria-label="Email notifications"
                  >
                    <span className="settings-toggle-knob" />
                  </button>
                </div>
                <div className="settings-row settings-row--wide">
                  <div>
                    <span className="settings-row-title">SMS alerts</span>
                    <span className="settings-row-caption">Critical plant and SLA breaches</span>
                  </div>
                  <button
                    type="button"
                    className={`settings-toggle${smsAlerts ? ' settings-toggle--on' : ''}`}
                    onClick={() => setSmsAlerts((v) => !v)}
                    aria-pressed={smsAlerts}
                    aria-label="SMS alerts"
                  >
                    <span className="settings-toggle-knob" />
                  </button>
                </div>
              </div>

              <div className="settings-pref-block settings-pref-block--spaced">
                <h3 className="settings-subhead">Regional & tables</h3>
                <div className="settings-field-grid settings-field-grid--narrow">
                  <div className="settings-field">
                    <label htmlFor="settings-lang">Language</label>
                    <select id="settings-lang" defaultValue="en">
                      <option value="en">English</option>
                      <option value="hi">हिन्दी</option>
                    </select>
                  </div>
                  <div className="settings-field">
                    <label htmlFor="settings-datefmt">Date format</label>
                    <select id="settings-datefmt" defaultValue="in">
                      <option value="in">DD/MM/YYYY</option>
                      <option value="iso">YYYY-MM-DD</option>
                    </select>
                  </div>
                </div>
                <div className="settings-row settings-row--wide settings-row--last">
                  <div>
                    <span className="settings-row-title">Compact tables</span>
                    <span className="settings-row-caption">Higher density lists and grids</span>
                  </div>
                  <button
                    type="button"
                    className={`settings-toggle${compactTables ? ' settings-toggle--on' : ''}`}
                    onClick={() => setCompactTables((v) => !v)}
                    aria-pressed={compactTables}
                    aria-label="Compact tables"
                  >
                    <span className="settings-toggle-knob" />
                  </button>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

export default Settings
